import { Check } from 'lucide-react';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CopyableText } from '~/components/common/copyable-text';
import { Badge } from '~/components/ui/badge';
import { useProductCategoryDisplay } from '~/features/catalog-operations/hooks/use-product-category-display';

import type { ProductDetail } from '../../types/product-overview.types';
import {
  formatFeedPrice,
  groupProductDetails,
  humanizeEnum,
  parseProductDescription,
} from '../../utils/product-detail';

/** Titled panel section wrapper. `count` renders a muted tally beside the
 *  heading (e.g. the number of grouped attributes). */
function Section({
  title,
  count,
  children,
}: {
  title: string;
  count?: number;
  children: ReactNode;
}) {
  return (
    <section className="border-border border-t px-5 py-4 first:border-t-0">
      <h3 className="text-foreground mb-3 flex items-center gap-2 text-sm font-semibold">
        {title}
        {count != null && <span className="text-muted-foreground font-normal">({count})</span>}
      </h3>
      {children}
    </section>
  );
}

/** A value chip — an outline Badge tinted with the primary token (border +
 *  text), rather than a solid fill, so chip clusters stay light-weight. */
function Chip({ children }: { children: ReactNode }) {
  return (
    <Badge variant="outline" className="border-primary text-primary rounded-sm font-normal">
      {children}
    </Badge>
  );
}

/** Checked-list highlights. Hidden entirely when the feed has none. */
export function HighlightsSection({ highlights }: { highlights: string[] }) {
  const { t } = useTranslation('onboarding');
  if (highlights.length === 0) return null;
  return (
    <Section title={t('productOverview.detail.sections.highlights')}>
      <ul className="bg-muted/40 space-y-2 rounded-lg px-4 py-3">
        {highlights.map((item, i) => (
          <li key={`${item}-${String(i)}`} className="flex items-start gap-2 text-sm">
            <Check aria-hidden="true" className="text-primary mt-0.5 h-4 w-4 shrink-0" />
            <span className="text-foreground leading-snug">{item}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

/** Definition grid of core specs. Empty fields are omitted (no blank rows);
 *  the whole section hides when nothing is populated. */
export function SpecificationsSection({ product }: { product: ProductDetail }) {
  const { t } = useTranslation('onboarding');
  // Google Product Category ships as a numeric GPC id — resolve to its readable name.
  const { display: categoryDisplay } = useProductCategoryDisplay(product.googleProductCategory);

  // Each row is [label, node]; sizes render as chips, everything else as text.
  const rows = useMemo(() => {
    const out: { label: string; value: ReactNode }[] = [];
    const push = (label: string, value: string) => {
      if (value.trim()) out.push({ label, value });
    };
    push(t('productOverview.detail.specs.category'), categoryDisplay);
    push(t('productOverview.detail.specs.productType'), product.productType);
    push(t('productOverview.detail.specs.color'), product.color);
    if (product.sizes.length > 0) {
      out.push({
        label: t('productOverview.detail.specs.sizes'),
        value: (
          <span className="flex flex-wrap justify-end gap-1.5">
            {product.sizes.map((s, i) => (
              <Chip key={`${s}-${String(i)}`}>{s}</Chip>
            ))}
          </span>
        ),
      });
    }
    push(t('productOverview.detail.specs.condition'), humanizeEnum(product.condition));
    push(t('productOverview.detail.specs.gender'), humanizeEnum(product.gender));
    push(t('productOverview.detail.specs.ageGroup'), humanizeEnum(product.ageGroup));
    push(t('productOverview.detail.specs.material'), product.material);
    push(t('productOverview.detail.specs.pattern'), product.pattern);
    return out;
  }, [product, t, categoryDisplay]);

  if (rows.length === 0) return null;

  return (
    <Section title={t('productOverview.detail.sections.specifications')}>
      <dl className="space-y-2.5 text-sm">
        {rows.map((row) => (
          <div key={row.label} className="flex items-start justify-between gap-4">
            <dt className="text-muted-foreground shrink-0">{row.label}</dt>
            <dd className="text-foreground min-w-0 text-right font-medium break-words">
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
    </Section>
  );
}

/** Grouped attributes (`product_details`) rendered as chip clusters. Header
 *  shows the number of distinct attributes. Hidden when there are none. */
export function AttributesSection({ product }: { product: ProductDetail }) {
  const { t } = useTranslation('onboarding');
  const groups = useMemo(
    () => groupProductDetails(product.productDetails),
    [product.productDetails],
  );
  if (groups.length === 0) return null;

  return (
    <Section title={t('productOverview.detail.sections.attributes')} count={groups.length}>
      <div className="space-y-2">
        {groups.map((group) => (
          <div
            key={group.name}
            className="bg-muted/40 flex items-start justify-between gap-4 rounded-lg px-3 py-2.5"
          >
            <p className="text-muted-foreground shrink-0 text-sm">{group.name}</p>
            <div className="flex flex-wrap justify-end gap-1.5">
              {group.values.map((value, i) => (
                <Chip key={`${value}-${String(i)}`}>{value}</Chip>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

/** Parsed description: lead paragraph, feature bullets, and an SEO keyword
 *  dump tucked into a collapsed disclosure. Hidden when fully empty. */
export function DescriptionSection({ description }: { description: string }) {
  const { t } = useTranslation('onboarding');
  const parsed = useMemo(() => parseProductDescription(description), [description]);

  if (!parsed.lead && parsed.bullets.length === 0 && parsed.keywords.length === 0) return null;

  return (
    <Section title={t('productOverview.detail.sections.description')}>
      {parsed.lead && <p className="text-foreground text-sm leading-relaxed">{parsed.lead}</p>}
      {parsed.bullets.length > 0 && (
        <ul className="text-foreground mt-2 list-disc space-y-1 pl-5 text-sm leading-snug">
          {parsed.bullets.map((b, i) => (
            <li key={`${b}-${String(i)}`}>{b}</li>
          ))}
        </ul>
      )}
      {/* SEO keyword dump surfaced as a labelled row of chips (per design). */}
      {parsed.keywords.length > 0 && (
        <div className="mt-4 flex items-start justify-between gap-4">
          <p className="text-muted-foreground shrink-0 text-sm">
            {t('productOverview.detail.description.searchKeywords')}
          </p>
          <div className="flex flex-wrap justify-end gap-1.5">
            {parsed.keywords.map((kw, i) => (
              <Chip key={`${kw}-${String(i)}`}>{kw}</Chip>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}

/** One identifier row. Empty values render a muted "Not set"; copyable rows
 *  expose the value via `CopyableText` (icon flips to a check on copy). */
function IdentifierRow({
  label,
  value,
  copyable = false,
}: {
  label: string;
  value: string;
  copyable?: boolean;
}) {
  const { t } = useTranslation('onboarding');
  return (
    <div className="grid grid-cols-[minmax(0,7rem)_1fr] items-center gap-x-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      {value.trim() ? (
        copyable ? (
          <CopyableText
            text={value}
            className="text-foreground font-medium break-all"
            copyMessage={t('productOverview.detail.identifiers.copied', { label })}
            ariaLabel={t('productOverview.detail.identifiers.copyAria', { label })}
          />
        ) : (
          <span className="text-foreground font-medium break-all">{value}</span>
        )
      ) : (
        <span className="text-muted-foreground italic">
          {t('productOverview.detail.identifiers.notSet')}
        </span>
      )}
    </div>
  );
}

/** Identifier block. Unlike the specs grid, empty values stay visible as
 *  "Not set" — a missing GTIN/MPN is information the user needs. */
export function IdentifiersSection({ product }: { product: ProductDetail }) {
  const { t } = useTranslation('onboarding');
  return (
    <Section title={t('productOverview.detail.sections.identifiers')}>
      <div className="space-y-2.5">
        <IdentifierRow
          label={t('productOverview.detail.identifiers.offerId')}
          value={product.offerId}
          copyable
        />
        <IdentifierRow
          label={t('productOverview.detail.identifiers.itemGroupId')}
          value={product.itemGroupId}
          copyable
        />
        {product.gtins.length > 0 ? (
          product.gtins.map((gtin, i) => (
            <IdentifierRow
              key={`${gtin}-${String(i)}`}
              label={t('productOverview.detail.identifiers.gtin')}
              value={gtin}
              copyable
            />
          ))
        ) : (
          <IdentifierRow label={t('productOverview.detail.identifiers.gtin')} value="" />
        )}
        <IdentifierRow label={t('productOverview.detail.identifiers.mpn')} value={product.mpn} />
        <IdentifierRow
          label={t('productOverview.detail.identifiers.salePrice')}
          value={formatFeedPrice(product.salePrice)}
        />
      </div>
    </Section>
  );
}
